import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'antd';
import { toast } from 'react-toastify';
import GenericTable from './GenericTable';
import Pagination from '../layout/Pagination';
import Spinner from '../elements/Spinner/Spinner';
import CreateSubscriptionPackageModal from '../modals/CreateSubscriptionPackageModal';
import { fetchProducts, updateProductIsSubscriptionOnly } from '../../api/webshop-products-orders-api';
import { formatPriceWithCurrency } from '../../utils/formatPrice';

const SubscriptionPackagesTable = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [creating, setCreating] = useState(false);

  const loadPackages = useCallback(async () => {
    setLoading(true);
    const { data, error } = await fetchProducts({
      page: currentPage,
      limit: 10,
      isSubscriptionOnlyProduct: true,
    });
    if (!error) {
      setProducts(data.products);
      setTotalPages(data.totalPages);
    } else {
      toast.error(error || 'Greška pri učitavanju paketa za pretplatu.');
    }
    setLoading(false);
  }, [currentPage]);

  useEffect(() => {
    loadPackages();
  }, [loadPackages]);

  const columns = [
    { key: 'name', label: 'Naziv' },
    {
      key: 'categories',
      label: 'Kategorija',
      render: (val) => (
        <span>{val && val.length > 0 ? val.map((c) => c?.name).join(', ') : '-'}</span>
      ),
    },
    {
      key: 'componentProducts',
      label: 'Proizvoda u paketu',
      render: (val, row) => <span>{row?.subscriptionPackage?.componentProducts?.length ?? '-'}</span>,
    },
    {
      key: 'pricing',
      label: 'Cene (3 / 6 / 12 meseci)',
      render: (val, row) => {
        const pricing = row?.subscriptionPackage?.pricing;
        if (!pricing) {
          return (
            <span
              style={{
                padding: '4px 12px',
                borderRadius: '16px',
                fontSize: '14px',
                fontWeight: '500',
                display: 'inline-block',
                backgroundColor: '#F2F4F7',
                color: '#475467',
              }}
            >
              Nije podešeno
            </span>
          );
        }
        return (
          <span>
            {formatPriceWithCurrency(pricing.threeMonth)} / {formatPriceWithCurrency(pricing.sixMonth)} /{' '}
            {formatPriceWithCurrency(pricing.twelveMonth)}
          </span>
        );
      },
    },
  ];

  const handleCreatePackage = async (productId) => {
    if (!productId) return;
    setCreating(true);
    const { success, message } = await updateProductIsSubscriptionOnly(productId, true);
    setCreating(false);
    if (success) {
      setCreateModalOpen(false);
      navigate(`/proizvodi/paketi/${productId}`);
    } else {
      toast.error(message || 'Greška pri kreiranju paketa.');
    }
  };

  const actions = [
    {
      key: 'edit',
      label: 'Uredi paket',
      icon: <img src="/assets/icons/edit-icon.svg" alt="Uredi" />,
      onClick: (row) => navigate(`/proizvodi/paketi/${row._id}`),
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '16px' }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button className="btn-primary" type="primary" onClick={() => setCreateModalOpen(true)}>
          + Dodaj paket
        </Button>
      </div>

      {loading ? (
        <Spinner size="lg" />
      ) : (
        <>
          <GenericTable
            data={products}
            columns={columns}
            actions={actions}
            emptyState={{
              icon: '/assets/icons/no-data.svg',
              title: 'Nema proizvoda označenih kao "Pretplatnički paket (3/6/12)"',
            }}
          />
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
        </>
      )}

      <CreateSubscriptionPackageModal
        visible={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onCreate={handleCreatePackage}
        creating={creating}
      />
    </div>
  );
};

export default SubscriptionPackagesTable;
